import { EFinancingProgram } from '@if/core/types'
import { WorksheetFormDefaultsDto, WorksheetFormDefaultsRequestDto } from '@src/types/WorksheetFormDefaultsDto'
import { QueryFunctionContext, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { getApiClient } from '../api-client'

const SCOPE = 'worksheetFormDefaults'

const keysFactory = {
  worksheetFormDefaults: (dto: WorksheetFormDefaultsRequestDto) => [{ scope: SCOPE, ...dto }] as const,
}

const getWorksheetFormDefaults = async ({
  queryKey: [dto],
}: QueryFunctionContext<
  ReturnType<(typeof keysFactory)['worksheetFormDefaults']>
>): Promise<WorksheetFormDefaultsDto> => {
  const apiClient = getApiClient()
  const response = await apiClient.post<WorksheetFormDefaultsDto>(
    `Worksheet/${EFinancingProgram.Personal}/WorksheetFormDefaults`,
    dto,
  )
  return response.data
}

const worksheetFormDefaultsQuery = (dto: WorksheetFormDefaultsRequestDto) => ({
  queryKey: keysFactory.worksheetFormDefaults(dto),
  queryFn: getWorksheetFormDefaults,
})

// react-hook-form's async `defaultValues` factory runs outside render, where hooks can't be
// called — this hook exists so the component only ever touches `useQueryClient()` through a
// named wrapper, and hands the factory a plain callback it can invoke later.
export const useWorksheetFormDefaultsSeed = () => {
  const queryClient = useQueryClient()
  const seedWorksheetFormDefaults = useCallback(
    async (dto: WorksheetFormDefaultsRequestDto) => {
      const { defaults } = await queryClient.fetchQuery(worksheetFormDefaultsQuery(dto))
      return defaults
    },
    [queryClient],
  )
  return { seedWorksheetFormDefaults }
}
